
/**
 * CSV Utility for client-side parsing.
 */

// Encoding map to support common Japanese encodings
const ENCODINGS = {
  'shift-jis': 'windows-31j',
  'utf-8': 'UTF-8'
};

/**
 * バイト列からUTF-8かどうかを判定する
 */
const detectIsUtf8 = (bytes: Uint8Array): boolean => {
  // BOM check
  if (bytes.length >= 3 && bytes[0] === 0xEF && bytes[1] === 0xBB && bytes[2] === 0xBF) return true;

  let i = 0;
  let multiByteCount = 0;
  while (i < Math.min(bytes.length, 4096)) {
    const b = bytes[i];
    if (b <= 0x7F) { i++; continue; }
    // UTF-8 multi-byte sequence validation
    let seqLen = 0;
    if ((b & 0xE0) === 0xC0) seqLen = 2;
    else if ((b & 0xF0) === 0xE0) seqLen = 3;
    else if ((b & 0xF8) === 0xF0) seqLen = 4;
    else return false; // Invalid UTF-8 lead byte

    if (i + seqLen > bytes.length) return false;
    for (let j = 1; j < seqLen; j++) {
      if ((bytes[i + j] & 0xC0) !== 0x80) return false;
    }
    multiByteCount++;
    i += seqLen;
  }
  return multiByteCount > 0;
};

export const readCsvFile = async (file: File, encoding: 'shift-jis' | 'utf-8' = 'shift-jis'): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const buffer = e.target?.result as ArrayBuffer;
        const bytes = new Uint8Array(buffer);

        // エンコーディング自動検出: UTF-8の特徴があればUTF-8として読む
        let effectiveEncoding = encoding;
        const isUtf8 = detectIsUtf8(bytes);
        if (encoding === 'shift-jis' && isUtf8) {
          effectiveEncoding = 'utf-8';
          console.log('[CSV] Auto-detected UTF-8 encoding');
        }

        const decoder = new TextDecoder(ENCODINGS[effectiveEncoding] || 'windows-31j');
        let text = decoder.decode(buffer);

        // Remove UTF-8 BOM if present
        if (text.startsWith('\uFEFF')) {
          text = text.substring(1);
        }

        // Sometimes BOM appears as garbled characters if encoding is mismatched initially
        text = text.replace(/^ï»¿/, '');

        // 文字化け検出: 置換文字（U+FFFD）が多い場合、逆のエンコーディングで再試行
        const replacementCount = (text.match(/\uFFFD/g) || []).length;
        if (replacementCount > 3) {
          const altEncoding = effectiveEncoding === 'utf-8' ? 'shift-jis' : 'utf-8';
          const altDecoder = new TextDecoder(ENCODINGS[altEncoding] || 'UTF-8');
          const altText = altDecoder.decode(buffer).replace(/^\uFEFF/, '').replace(/^ï»¿/, '');
          const altReplacementCount = (altText.match(/\uFFFD/g) || []).length;
          if (altReplacementCount < replacementCount) {
            console.log(`[CSV] Re-decoded with ${altEncoding} (fewer garbled chars: ${altReplacementCount} vs ${replacementCount})`);
            text = altText;
          }
        }

        console.log(`[CSV] Read file with ${effectiveEncoding}. Length: ${text.length}`);
        resolve(text);
      } catch (err) {
        console.error("[CSV] Decoding error:", err);
        reject(err);
      }
    };

    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
};

export const parseCsv = (text: string): string[][] => {
  const result: string[][] = [];
  let currentRow: string[] = [];
  let currentField = '';
  let insideQuote = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];

    if (char === '"') {
      if (insideQuote && nextChar === '"') {
        currentField += '"';
        i++;
      } else {
        insideQuote = !insideQuote;
      }
    } else if (char === ',' && !insideQuote) {
      currentRow.push(currentField);
      currentField = '';
    } else if ((char === '\r' || char === '\n') && !insideQuote) {
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
      currentRow.push(currentField);
      if (currentRow.length > 1 || (currentRow.length === 1 && currentRow[0] !== '')) {
          result.push(currentRow);
      }
      currentRow = [];
      currentField = '';
    } else {
      currentField += char;
    }
  }

  if (currentField || currentRow.length > 0) {
    currentRow.push(currentField);
    result.push(currentRow);
  }

  return result;
};
